// Client tipado de `loan-collector-assignment-business`: una función por operationId del OpenAPI
// público. Alimenta la pantalla Cobranza del Portal Admin SureKeep.
//
// TIPOS — de dónde salen y por qué:
//   El OpenAPI de este lambda declara TODAS las respuestas como `ApiResponse` genérico, así que un
//   `*.gen.ts` no daría ninguna entidad. Las entidades se importan de `@fiado/type-kit`; aquí solo
//   viven los sobres de lista (`contract.ts`), que el type-kit no publica.
//
//   ⚠️ REGLA DE IMPORT (bundle): `import type` para los DTOs — TS los borra en build, costo CERO.
//   NUNCA importar el barrel `@fiado/type-kit/bin/loanCollectorAssignment` como valor: re-exporta
//   los DTOs y arrastra `class-validator` + `class-transformer` al bundle del browser. Los enums sí
//   se pueden importar como valor, pero SIEMPRE por deep-path (son módulos standalone).
import type { CreateAssignmentRuleRequest } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/requests/CreateAssignmentRuleRequest';
import type { UpdateAssignmentRuleRequest } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/requests/UpdateAssignmentRuleRequest';
import type { AssignmentRuleResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/AssignmentRuleResponse';
import type { CollectorAssignmentResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/CollectorAssignmentResponse';
import type { RedistributeSummaryResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/RedistributeSummaryResponse';
import { apiFetch } from '../../client';
import type {
    AssignmentRulesResponse,
    CollectorAssignmentQuery,
    CollectorAssignmentsResponse,
    CollectorLoadsResponse,
} from './contract';

const BASE_URL = import.meta.env.VITE_API_LOAN_COLLECTOR_ASSIGNMENT_BASE_URL;

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

/** `?a=1&b=2`, o cadena vacía si no hay nada que mandar. */
function queryString(params: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) search.set(key, String(value));
    }
    const rendered = search.toString();
    return rendered ? `?${rendered}` : '';
}

/**
 * `publicListCollectorLoads` — GET /retailers/{retailerId}/collector-loads. La carga activa de cada
 * cobrador. Permiso `loan.collectorAssignment.read`, scope TENANT.
 */
export function listCollectorLoads(retailerId: string): Promise<CollectorLoadsResponse> {
    return call<CollectorLoadsResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/collector-loads`,
    );
}

/**
 * `publicListCollectorAssignments` — GET /retailers/{retailerId}/collector-assignments. Historial
 * append-only: un crédito puede tener varias filas y solo una `ACTIVE`. Permiso
 * `loan.collectorAssignment.read`.
 */
export function listCollectorAssignments(
    retailerId: string,
    query: CollectorAssignmentQuery = {},
): Promise<CollectorAssignmentsResponse> {
    return call<CollectorAssignmentsResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/collector-assignments${queryString({
            status: query.status,
            storeId: query.storeId,
            collectorUserId: query.collectorUserId,
        })}`,
    );
}

/**
 * `publicListAssignmentRules` — GET /retailers/{retailerId}/assignment-rules. Todas las reglas del
 * retailer, activa e históricas. Permiso `loan.assignmentRule.read`.
 */
export function listAssignmentRules(retailerId: string): Promise<AssignmentRulesResponse> {
    return call<AssignmentRulesResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/assignment-rules`,
    );
}

/**
 * `publicGetAssignmentRule` — GET /retailers/{retailerId}/assignment-rules/{ruleId}.
 * Permiso `loan.assignmentRule.read`.
 */
export function getAssignmentRule(
    retailerId: string,
    ruleId: string,
): Promise<AssignmentRuleResponse> {
    return call<AssignmentRuleResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/assignment-rules/${encodeURIComponent(ruleId)}`,
    );
}

/**
 * `publicCreateAssignmentRule` — POST /retailers/{retailerId}/assignment-rules (201). Con
 * `active: true` desactiva la regla activa anterior. Permiso `loan.assignmentRule.create`.
 */
export function createAssignmentRule(
    retailerId: string,
    body: CreateAssignmentRuleRequest,
): Promise<AssignmentRuleResponse> {
    return call<AssignmentRuleResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/assignment-rules`,
        { method: 'POST', body: JSON.stringify(body) },
    );
}

/**
 * `publicUpdateAssignmentRule` — PUT /retailers/{retailerId}/assignment-rules/{ruleId}. Update
 * parcial. Permiso `loan.assignmentRule.update`.
 */
export function updateAssignmentRule(
    retailerId: string,
    ruleId: string,
    body: UpdateAssignmentRuleRequest,
): Promise<AssignmentRuleResponse> {
    return call<AssignmentRuleResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/assignment-rules/${encodeURIComponent(ruleId)}`,
        { method: 'PUT', body: JSON.stringify(body) },
    );
}

/**
 * `publicActivateAssignmentRule` — PUT /retailers/{retailerId}/assignment-rules/{ruleId}/activate.
 * Sin body. Permiso `loan.assignmentRule.update`.
 */
export function activateAssignmentRule(
    retailerId: string,
    ruleId: string,
): Promise<AssignmentRuleResponse> {
    return call<AssignmentRuleResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/assignment-rules/${encodeURIComponent(ruleId)}/activate`,
        { method: 'PUT' },
    );
}

/**
 * `publicReassignAssignment` — POST /retailers/{retailerId}/collector-assignments/{creditId}/reassign.
 * El path va por CRÉDITO, no por `assignmentId`. `reason` es obligatorio (auditoría de acción
 * crítica). Permiso `loan.collectorAssignment.reassign`.
 */
export function reassignAssignment(
    retailerId: string,
    creditId: string,
    reason: string,
): Promise<CollectorAssignmentResponse> {
    return call<CollectorAssignmentResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/collector-assignments/${encodeURIComponent(creditId)}/reassign`,
        { method: 'POST', body: JSON.stringify({ reason }) },
    );
}

/**
 * `publicRedistributeCollector` — POST /retailers/{retailerId}/collectors/{userId}/redistribute.
 * Reparte la cartera activa del cobrador. Un 200 puede traer `errors`: no todos los créditos
 * necesariamente se movieron. Permiso `loan.collectorAssignment.redistribute`.
 */
export function redistributeCollector(
    retailerId: string,
    collectorUserId: string,
    reason: string,
): Promise<RedistributeSummaryResponse> {
    return call<RedistributeSummaryResponse>(
        `/retailers/${encodeURIComponent(retailerId)}/collectors/${encodeURIComponent(collectorUserId)}/redistribute`,
        { method: 'POST', body: JSON.stringify({ reason }) },
    );
}

export const collectorAssignmentApi = {
    listCollectorLoads,
    listCollectorAssignments,
    listAssignmentRules,
    getAssignmentRule,
    createAssignmentRule,
    updateAssignmentRule,
    activateAssignmentRule,
    reassignAssignment,
    redistributeCollector,
};

export type CollectorAssignmentApi = typeof collectorAssignmentApi;
